import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  SNAG_SEVERITIES,
  createSnag,
  deleteSnag,
  listQuality,
  listSnags,
  moveSnag,
  updateSnag,
  type Ncr,
  type ProjectMember,
  type Severity,
  type Snag,
  type SnagStatus,
} from '../api.js';
import { formatDate, openFor } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { CrossLink, focusClass, useArrivedToCreate, useFocusRow } from './cross-links.js';
import { useProjectMembers } from './ProjectMembers.js';
import { recordHref, sectionHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * Snags (SN-01 / SN-03).
 *
 * `03` SN-03 calls raising one "the fastest form in the product", so the form
 * is one box and a severity, with nothing to click first.
 *
 * **There is no "verify" button for the person the snag is assigned to.** The
 * server leaves it out of `allowed_next`, and refuses the move even if a client
 * asks for it anyway. A check by the person who did the work is not a check.
 *
 * ON THE REGISTER PATTERN — and what happened to "show the finished ones"
 * ----------------------------------------------------------------------
 * This register opened on the unfinished snags, with a link to reveal the rest.
 * That is still the default, but it is now a FILTER with the toolbar's count
 * beside it — "Showing 6 of 34 snags · Show all" — rather than a toggle whose
 * off state was invisible. The register hid twenty-eight records and said so
 * only in the wording of a link.
 *
 * The row's one action is the next step in the snag's own life: start fixing
 * it, submit the fix, then check and close it. Sending a fix back, accepting a
 * snag as it is and voiding one all stay in the overflow menu, and all three
 * need words on the record before they happen.
 */

const STATUS_LABELS: Readonly<Record<SnagStatus, string>> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'Being fixed',
  closure_submitted: 'Fixed — waiting to be checked',
  verified_closed: 'Fixed and checked',
  accepted_as_is: 'Accepted as it is',
  void: 'Void',
};

const STATUS_SYMBOLS: Readonly<Record<SnagStatus, string>> = {
  open: '○',
  assigned: '◐',
  in_progress: '◐',
  closure_submitted: '⊙',
  verified_closed: '✓',
  accepted_as_is: '≈',
  void: '×',
};

const SEVERITY_LABELS: Readonly<Record<Severity, string>> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const MOVE_LABELS: Partial<Record<SnagStatus, string>> = {
  assigned: 'Send back',
  in_progress: 'Start fixing',
  closure_submitted: 'Submit the fix',
  verified_closed: 'Check and close',
  accepted_as_is: 'Accept as it is',
  void: 'Void',
};

/** Moves that need words on the record before they happen. */
const NEEDS_WORDS: readonly SnagStatus[] = ['closure_submitted', 'accepted_as_is', 'void'];

/** Nothing is waiting on these any more. */
const CLOSED: readonly SnagStatus[] = ['verified_closed', 'accepted_as_is', 'void'];

/**
 * The lifecycle forwards, for picking the row's one action.
 *
 * `assigned` is absent because it means two different things: from a submitted
 * fix it is "send it back", and from `open` it would be "assign it" with nobody
 * named — and naming somebody is what assigns a snag, which is the form's job.
 * `accepted_as_is` and `void` are absent because both are endings that need a
 * written reason.
 */
const FORWARD: readonly SnagStatus[] = ['in_progress', 'closure_submitted', 'verified_closed'];

/** Worst first, which is the order the register is read in. */
const SEVERITY_ORDER: Readonly<Record<Severity, number>> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const MIN_REASON = 20;
const MIN_NOTE = 10;

interface Props {
  readonly projectId: string;
  /**
   * One snag, as its own page, from `/projects/<id>/quality/snags/<recordId>`.
   *
   * A snag EARNS a page, and for a reason the drawer made worse: it is made of
   * BEFORE-AND-AFTER PHOTOS and a list of checks, and a snag that has gone round
   * three times has three of each. Stacked in a 480px panel that is a scroll;
   * side by side it is the argument the record exists to settle.
   *
   * It also fixes something `?focus=` could never do here. This register opens
   * on the UNFINISHED snags, so a cross-link at a closed one landed on a
   * register that had filtered it out — a link to nothing. A page has no filter
   * to be excluded by.
   */
  readonly recordId?: string | null;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    /** `snag: X`. Gates the button; the rows are already filtered. */
    readonly exportRows: boolean;
  };
}

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` is gone: the snag has a page and an address now (§4.3). The forms
 * stay — and `words` above all, because sending a fix back and accepting a snag
 * as-is both demand somebody's written reason on the record.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'words'; readonly id: string; readonly to: SnagStatus }
  | { readonly kind: 'add' };

export function Snags({ projectId, recordId = null, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Snag[] | null>(null);
  /** How many exist on the server, vs how many are loaded — see `api/paging.ts`. */
  const [registerTotal, setRegisterTotal] = useState(0);
  const [wantAll, setWantAll] = useState(false);
  /** The page's one member fetch — its own when this renders standalone. */
  const people = useProjectMembers(projectId);
  /** Not the same as an empty register — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  // Opens on the unfinished ones, and the toolbar says so. See the note above.
  const view = useRegisterView('severity', { stage: 'unfinished' });
  /**
   * The NCRs, for the location line on each snag.
   *
   * THERE IS NO LINK BETWEEN A SNAG AND AN NCR in the data model, and this does
   * not invent one. What the two share is a PLACE, and "there is an open NCR in
   * this room" is worth knowing when you are looking at a snag in it — so the
   * sentence says "at this location" and claims nothing more. Silent on
   * failure: a client raises snags and reads no NCRs.
   */
  const [ncrs, setNcrs] = useState<readonly Ncr[]>([]);

  const load = useCallback(async () => {
    try {
      const page = await listSnags(projectId, wantAll ? LOAD_ALL_LIMIT : undefined);
      setItems(page.rows);
      setRegisterTotal(page.total);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the snags.');
      setItems([]);
    }
    try {
      setNcrs((await listQuality(projectId)).ncrs);
    } catch {
      setNcrs([]);
    }
  }, [projectId, wantAll]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<readonly Snag[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setItems(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /** `?new=snag` — arrived from the site home to raise one. */
  const startNew = useCallback(() => setDrawer({ kind: 'add' }), []);
  useArrivedToCreate('snag', startNew);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!items) return [];

    const stage = view.chosen.stage;
    const severity = view.chosen.severity;

    const filtered = items.filter((s) => {
      // Everything somebody would type: the number, what is wrong, who has it
      // and where it is.
      const hay = [s.number, s.description, s.assignee_name, s.location_path]
        .filter(Boolean)
        .join(' ');
      if (!matches(hay, view.query)) return false;
      if (severity && s.severity !== severity) return false;
      if (stage === 'unfinished' && CLOSED.includes(s.status)) return false;
      if (stage === 'overdue' && !s.overdue) return false;
      if (stage === 'waiting_check' && s.status !== 'closure_submitted') return false;
      if (stage === 'finished' && !CLOSED.includes(s.status)) return false;
      return true;
    });

    const compare: Readonly<Record<string, (a: Snag, b: Snag) => number>> = {
      // Worst first, and within a severity the oldest first — which is the
      // order somebody walking a site would want the list in.
      severity: (a, b) =>
        SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || b.days_open - a.days_open,
      age: (a, b) => b.days_open - a.days_open,
      number: (a, b) => a.number.localeCompare(b.number),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.severity!);
  }, [items, view.query, view.chosen.stage, view.chosen.severity, view.sortKey]);

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add' ? (items?.find((s) => s.id === drawer.id) ?? null) : null;

  /**
   * Make a move, asking for words first where the record needs them.
   *
   * A send-back is in that list too: the note on a rejected fix is the one that
   * matters when a snag goes round for the third time.
   */
  function move(snag: Snag, to: SnagStatus): void {
    setError(null);
    if (NEEDS_WORDS.includes(to) || to === 'assigned') {
      setDrawer({ kind: 'words', id: snag.id, to });
      return;
    }
    void run(() => moveSnag(projectId, snag.id, { status: to }));
  }

  /**
   * The moves this snag is actually offering, with the one ambiguous case out.
   *
   * `→ assigned` from anywhere except a submitted fix would read as "assign it"
   * with nobody to assign to.
   */
  function movesFor(snag: Snag): readonly SnagStatus[] {
    return snag.allowed_next.filter(
      (to) => !(to === 'assigned' && snag.status !== 'closure_submitted'),
    );
  }

  /** The one move that goes on the row. */
  function primaryFor(snag: Snag): RowAction {
    const moves = movesFor(snag);
    const forward = FORWARD.find((status) => moves.includes(status));
    if (forward) {
      return {
        label: MOVE_LABELS[forward] ?? forward,
        disabled: busy,
        onSelect: () => move(snag, forward),
      };
    }
    return {
      label: 'Open',
      onSelect: () => navigate(recordHref(projectId, 'snags', snag.id)),
    };
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page. Two entries differ, and
   * both for the same reason: on the page you are already looking at the record.
   */
  function overflowFor(
    snag: Snag,
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = onPage
      ? []
      : [{ label: 'Open', onSelect: () => navigate(recordHref(projectId, 'snags', snag.id)) }];
    if (can.edit && !CLOSED.includes(snag.status)) {
      actions.push({ label: 'Edit', onSelect: () => setDrawer({ kind: 'edit', id: snag.id }) });
    }
    // The register never offers a move the machine has not returned, so a
    // button here cannot be one the service then refuses.
    for (const to of movesFor(snag)) {
      actions.push({
        label: MOVE_LABELS[to] ?? to,
        danger: to === 'void',
        disabled: busy,
        onSelect: () => move(snag, to),
      });
    }
    if (can.remove) {
      actions.push({
        label: 'Delete',
        danger: true,
        disabled: busy,
        onSelect: () => {
          void run(() => deleteSnag(projectId, snag.id)).then((ok) => {
            // Deleting the record you are standing on leaves you on an address
            // that names nothing. The register is where the deletion makes
            // sense, so that is where it puts you.
            if (ok && onPage) navigate(sectionHref(projectId, 'quality'));
          });
        },
      });
    }
    // Whatever is already on the row must not also be in the menu behind it.
    return primary ? actions.filter((action) => action.label !== primary.label) : actions;
  }

  async function exportRows(): Promise<void> {
    const headers = [
      'Snag',
      'What is wrong',
      'Severity',
      'Status',
      'With',
      'Where',
      'Days open',
      'Over the limit',
      'Due',
      'Fixed',
      'Accepted because',
      'Void because',
    ];
    const rows = shown.map((s) => [
      s.number,
      s.description,
      SEVERITY_LABELS[s.severity],
      STATUS_LABELS[s.status],
      s.assignee_name ?? '',
      s.location_path ?? '',
      String(s.days_open),
      s.overdue ? `Over the ${s.age_threshold}-day limit` : '',
      s.due_date ?? '',
      s.closure_note ?? '',
      s.accepted_as_is_reason ?? '',
      s.void_reason ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'snag',
        filename: `snags-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Snags</h3>
      {items && can.write && (
        <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
          Raise a snag
        </button>
      )}
    </div>
  );

  /*
   * The same four states on a record page, still four different sentences. The
   * furniture above them changes: "Raise a snag" is the wrong offer to somebody
   * who followed a link to one particular snag.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="snags" /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s snags.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {above}
        <p aria-live="polite">Loading {recordId ? 'the snag' : 'the snags'}…</p>
      </>
    );
  }

  const open = items.filter((s) => !CLOSED.includes(s.status));
  const serious = open.filter((s) => s.severity === 'high' || s.severity === 'critical');
  const overdue = open.filter((s) => s.overdue);

  /**
   * The three forms, shared by the register and the record page.
   *
   * `WordsForm` above all: submitting a fix, accepting a snag as it is and
   * voiding one each put somebody's words on the record, and `05` sets the
   * minimum length. A page with its own copy is a second place for that to
   * drift.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Raise a snag" dirty={formDirty} onClose={closeDrawer}>
          <SnagForm
            people={people}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createSnag(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer
          title={`Edit ${subject.number}`}
          subtitle={subject.description}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <SnagForm
            existing={subject}
            people={people}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              // The version this form was OPENED on. If somebody else has saved
              // since, the server refuses with a 409 rather than letting this
              // form's copy of the description and the assignee overwrite them.
              if (await run(() => updateSnag(projectId, subject.id, input, subject.record_version)))
                closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'words' && subject && (
        <Drawer
          title={`${MOVE_LABELS[drawer.to] ?? drawer.to} — ${subject.number}`}
          subtitle={subject.description}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <WordsForm
            snag={subject}
            to={drawer.to}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              const to = drawer.to;
              if (await run(() => moveSnag(projectId, subject.id, { status: to, ...input }))) {
                closeDrawer();
              }
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ---------------------------------------------------------- one snag, in full
  if (recordId) {
    const record = items.find((s) => s.id === recordId) ?? null;
    if (!record) {
      /* A FAILED LOAD IS NOT A MISSING RECORD — see `Materials.tsx`. */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="snags" />
      );
    }

    /*
     * `primaryFor` falls back to "Open" once a snag is finished. On the record
     * page that is a button to the page you are already on, so it goes.
     */
    const forward = primaryFor(record);
    const primary = forward.label === 'Open' ? null : forward;
    /* NO BAR WHEN THERE IS NOTHING IN IT — see `Drawings.tsx`. */
    const more = overflowFor(record, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={record.number} {...(primary ? { primary } : {})} more={more} />
      ) : null;

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="snags"
          reference={record.number}
          title={record.description}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              {SEVERITY_LABELS[record.severity]}
              {' · '}
              {record.assignee_name ?? 'Nobody assigned'}
              {/*
                The age stays visible here for the same reason it stays on the
                row at both densities: a snag past its limit is the fact that
                changes what somebody does next. In words, never colour alone.
              */}
              {record.overdue && (
                <span className="task-overdue">
                  {' · '}
                  {record.days_open} days open, over the {record.age_threshold}-day limit
                </span>
              )}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<SnagProperties snag={record} />}
        >
          {/*
            THE PHOTOS COME FIRST, and that is the whole argument for this record
            having a page. A snag IS a before-and-after photograph; the words are
            the caption. On the drawer they were the last thing down.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Photos</h4>
            </div>
            {/*
              Shared by default: the client raised half of these and is entitled
              to see the evidence they were fixed.
            */}
            <Attachments
              projectId={projectId}
              entityType="snag"
              entityId={record.id}
              can={{ write: can.edit }}
              noun="photos"
              shareByDefault
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Checks</h4>
            </div>
            {record.verifications.length > 0 ? (
              <>
                <ul className="snag-checks">
                  {record.verifications.map((v) => (
                    <li key={v.id}>
                      <strong>{v.outcome === 'passed' ? 'Checked' : 'Sent back'}</strong> by{' '}
                      {v.verifier_name ?? 'someone'}
                      {v.comment ? ` — ${v.comment}` : ''}
                    </li>
                  ))}
                </ul>
                <p className="hint">
                  A rejected fix is kept as a check too. It is the one that matters when a snag
                  goes round for the third time.
                </p>
              </>
            ) : (
              /*
               * An empty state must name an action ITS reader can take, and this
               * one cannot name "check it": the person the snag is assigned to
               * may never verify their own fix, and the server refuses it. So it
               * says what has to happen first and who does it.
               */
              <p className="hint">
                Nobody has checked this yet. A fix is submitted by whoever did the work and then
                checked by somebody else — never by the same person.
              </p>
            )}
          </div>

          {/*
            NOT A RELATIONSHIP THE DATABASE HOLDS. What a snag and an NCR share
            is a PLACE, and this says exactly that and claims nothing more.
            Rendered only when there is something to say, because a section
            headed "Nearby" that is always empty is furniture.
          */}
          <NcrsHere projectId={projectId} snag={record} ncrs={ncrs} onPage />
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {items.length > 0 && (
        <p className="hint" role="status">
          <strong>{open.length} open</strong>
          {serious.length > 0 && (
            <>
              {' · '}
              <span className="task-overdue">{serious.length} serious</span>
            </>
          )}
          {overdue.length > 0 && <> · {overdue.length} past the age for their severity</>}
          . The Snagging stage needs every serious one closed, and every minor one closed or
          accepted.
        </p>
      )}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No snags raised.</strong>
          </p>
          <p>
            {can.write
              ? 'Raise one with a line saying what is wrong and how serious it is. Whoever is assigned it fixes it — and somebody else has to check it, which is what makes a closed snag mean anything.'
              : /*
                 * An empty state must name an action ITS READER can take. A
                 * Site Engineer or a QS on `snag: V` cannot raise one, so the
                 * sentence for them says who does.
                 */
                'Nobody has raised one. Site, QA/QC, the Project Manager and the client can all raise a snag — and whoever is assigned it fixes it, with somebody else checking, which is what makes a closed snag mean anything.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search snags"
            searchPlaceholder="Number, what is wrong, who has it"
            shown={shown.length}
            total={items.length}
            registerTotal={registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && items.length < registerTotal}
            noun={['snag', 'snags']}
            filters={[
              {
                key: 'stage',
                label: 'Show',
                options: [
                  { value: 'unfinished', label: 'Not finished' },
                  { value: 'overdue', label: 'Past the age limit' },
                  { value: 'waiting_check', label: 'Waiting to be checked' },
                  { value: 'finished', label: 'Finished' },
                ],
              },
              {
                key: 'severity',
                label: 'Severity',
                options: SNAG_SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABELS[s] })),
              },
            ]}
            sorts={[
              { key: 'severity', label: 'Most serious' },
              { key: 'age', label: 'Longest open' },
              { key: 'number', label: 'Reference' },
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === items.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no snags raised". This reader has a
             * register full of snags and a filter that excludes all of them —
             * and on this register that is the ordinary case, because it opens
             * with the finished ones held back.
             */
            <div className="empty">
              <p>
                <strong>No snag matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {items.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  Snags, with their severity, who has them, and how long they have been open.{' '}
                  {shown.length} of {items.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Snag</th>
                    <th scope="col">Severity</th>
                    <th scope="col">With</th>
                    <th scope="col">Open for</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s) => {
                    const primary = primaryFor(s);
                    return (
                      <SnagRow key={s.id} snag={s}>
                        <td>
                          <span className="ms-symbol" aria-hidden="true">
                            {STATUS_SYMBOLS[s.status]}
                          </span>{' '}
                          {/*
                            A REAL LINK, because the record has an address now —
                            so a middle-click, a new tab and a copied link all
                            mean what they look like they mean.
                          */}
                          <AppLink className="rk-open" to={recordHref(projectId, 'snags', s.id)}>
                            <strong>{s.number}</strong> {s.description}
                            <span className="visually-hidden"> — open this snag</span>
                          </AppLink>
                          <span className="tree-code"> {STATUS_LABELS[s.status]}</span>
                          {/*
                            SECONDARY. Hidden at compact density, and all of it
                            is on the record's drawer as well, so nothing is
                            visible at only one density.
                          */}
                          <span className="rk-secondary">
                            {s.location_path && (
                              <span className="tree-code"> {s.location_path}</span>
                            )}
                            <NcrsHere projectId={projectId} snag={s} ncrs={ncrs} />
                            {s.closure_note && (
                              <span className="snag-note">Fixed: {s.closure_note}</span>
                            )}
                            {s.accepted_as_is_reason && (
                              <span className="snag-note">
                                Accepted: {s.accepted_as_is_reason}
                              </span>
                            )}
                            {s.void_reason && (
                              <span className="snag-note">Void: {s.void_reason}</span>
                            )}
                            {s.verifications.map((v) => (
                              <span className="snag-check" key={v.id}>
                                {v.outcome === 'passed' ? 'Checked' : 'Sent back'} by{' '}
                                {v.verifier_name ?? 'someone'}
                                {v.comment ? ` — ${v.comment}` : ''}
                              </span>
                            ))}
                          </span>
                        </td>
                        <td data-label="Severity">{SEVERITY_LABELS[s.severity]}</td>
                        <td data-label="With">{s.assignee_name ?? '—'}</td>
                        <td data-label="Open for">
                          {openFor(s.days_open)}
                          {/*
                            Stays at BOTH densities. It is the fact on the row
                            that changes what somebody does next.
                          */}
                          {s.overdue && (
                            <span className="task-overdue">
                              {' '}
                              · over the {s.age_threshold}-day limit
                            </span>
                          )}
                        </td>
                        <td>
                          <RowActions
                            rowLabel={s.number}
                            primary={primary}
                            more={overflowFor(s, primary)}
                          />
                        </td>
                      </SnagRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the snag IS — the record page's rail.
 *
 * Everything compact density hides is here. It was the drawer's whole body until
 * the snag earned a page; the photos and the checks moved to the main column,
 * which is the right way round — a snag IS a before-and-after photograph and the
 * words are the caption, and in the drawer the photographs were last.
 */
function SnagProperties({ snag }: { readonly snag: Snag }): JSX.Element {
  return (
    <>
      <dl className="detail-grid">
        <dt>Severity</dt>
        <dd>{SEVERITY_LABELS[snag.severity]}</dd>
        <dt>With</dt>
        <dd>{snag.assignee_name ?? '—'}</dd>
        <dt>Open for</dt>
        <dd>
          {openFor(snag.days_open)}
          {snag.overdue && (
            <span className="task-overdue"> · over the {snag.age_threshold}-day limit</span>
          )}
        </dd>
        <dt>Due</dt>
        <dd>{formatDate(snag.due_date)}</dd>
        {snag.location_path && (
          <>
            <dt>Where</dt>
            <dd>{snag.location_path}</dd>
          </>
        )}
        {snag.closure_note && (
          <>
            <dt>Fixed</dt>
            <dd>{snag.closure_note}</dd>
          </>
        )}
        {snag.accepted_as_is_reason && (
          <>
            <dt>Accepted because</dt>
            <dd>{snag.accepted_as_is_reason}</dd>
          </>
        )}
        {snag.void_reason && (
          <>
            <dt>Voided because</dt>
            <dd>{snag.void_reason}</dd>
          </>
        )}
      </dl>
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * Open NCRs in the same place as this snag.
 *
 * NOT A RELATIONSHIP THE DATABASE HOLDS. An NCR and a snag are separate records
 * with no link between them, and this does not pretend otherwise — it matches
 * on `location_id` and says "at this location", which is what it matched. It
 * earns its place because a snag in a room that already has an open NCR is
 * usually the same underlying problem, and the two registers sit one above the
 * other in the Quality section without either mentioning the other.
 */
function NcrsHere({
  projectId,
  snag,
  ncrs,
  /**
   * Render it as one of the record page's own sections rather than as a line.
   *
   * Still ABSENT when there is nothing to say. A section headed "Nearby" that is
   * empty on nine snags out of ten is furniture, and it would also invite the
   * reader to think the emptiness means something — which it does not, because
   * this is a coincidence of place and not a relationship.
   */
  onPage = false,
}: {
  readonly projectId: string;
  readonly snag: Snag;
  readonly ncrs: readonly Ncr[];
  readonly onPage?: boolean;
}): JSX.Element | null {
  if (!snag.location_id) return null;
  const open = ncrs.filter(
    (n) => n.location_id === snag.location_id && n.status !== 'verified' && n.status !== 'void',
  );
  if (open.length === 0) return null;

  const list = (
    <span className="snag-note">
      Open NCRs at this location:{' '}
      {open.map((n, index) => (
        <span key={n.id}>
          {index > 0 && ', '}
          <CrossLink
            projectId={projectId}
            section="quality"
            focus={n.id}
            says="an open NCR recorded at the same location as this snag"
          >
            {n.number}
          </CrossLink>
        </span>
      ))}
    </span>
  );

  if (!onPage) return list;

  return (
    <div className="register record-section">
      <div className="section-head sub-head-row">
        <h4 className="sub-head">Also at this location</h4>
      </div>
      <p className="hint">{list}</p>
    </div>
  );
}

/** One row, which knows whether a cross-link came looking for it. */
function SnagRow({
  snag,
  children,
}: {
  readonly snag: Snag;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(snag.id);
  return (
    <tr
      ref={ref}
      className={focusClass(
        focused,
        `snag-${snag.status} sev-${snag.severity}${snag.overdue ? ' snag-overdue' : ''}`,
      )}
      aria-current={focused ? 'true' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

interface WordsProps {
  readonly snag: Snag;
  readonly to: SnagStatus;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: { reason?: string; closure_note?: string }) => void | Promise<void>;
}

/** The one form that covers "what did you do", "why accept it" and "why void it". */
function WordsForm({
  snag,
  to,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: WordsProps): JSX.Element {
  const [text, setText] = useState('');
  const isFix = to === 'closure_submitted';
  const isSendBack = to === 'assigned';
  const serious = snag.severity === 'high' || snag.severity === 'critical';

  const minimum = isFix ? (serious ? MIN_NOTE : 1) : MIN_REASON;

  useEffect(() => {
    onDirtyChange?.(text !== '');
  }, [text, onDirtyChange]);

  const explanation = isFix
    ? serious
      ? 'A high or critical snag cannot be submitted as fixed without saying what was done. Attach an after-photo as well — whoever checks it will look for one.'
      : 'Say what was done, so whoever checks it knows what to look at.'
    : isSendBack
      ? 'Say what is still wrong. It goes on the record, and the person fixing it sees it.'
      : to === 'accepted_as_is'
        ? 'Accepting a snag closes it WITHOUT pretending it was fixed. That is the honest ending when the client has agreed to live with it — and it needs a reason.'
        : 'A voided snag keeps its number. The record has to say why it was dropped.';

  return (
    // The drawer's own heading already says which snag and which move, so the
    // panel does not say it a second time.
    <div className="panel">
      <p className="hint">{explanation}</p>

      <div className="field">
        <label htmlFor="snag-words">
          {isFix ? 'What was done?' : isSendBack ? 'What is still wrong?' : 'Why?'}
        </label>
        <textarea
          id="snag-words"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={4000}
          disabled={busy}
          placeholder={
            isFix
              ? 'Grout raked out and replaced, joint re-cut to line.'
              : isSendBack
                ? 'Joint is still proud of the tile by 2mm along the north edge.'
                : 'Client walked it and agreed to live with the joint as built.'
          }
        />
        <p className="hint">
          {text.trim().length}/{minimum} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || text.trim().length < minimum}
          onClick={() =>
            void onSubmit(isFix ? { closure_note: text.trim() } : { reason: text.trim() })
          }
        >
          {busy ? 'Saving…' : (MOVE_LABELS[to] ?? 'Save')}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FormValues {
  readonly description: string;
  readonly severity: Severity;
  readonly assignee_id: string | null;
  readonly due_date: string | null;
}

interface FormProps {
  readonly existing?: Snag;
  readonly people: readonly ProjectMember[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: FormValues) => void | Promise<void>;
}

function SnagForm({
  existing,
  people,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: FormProps): JSX.Element {
  const [description, setDescription] = useState(existing?.description ?? '');
  const [severity, setSeverity] = useState<Severity>(existing?.severity ?? 'medium');
  const [assignee, setAssignee] = useState(existing?.assignee_id ?? '');
  const [due, setDue] = useState(existing?.due_date ?? '');

  /*
   * Compared against the record rather than tracked with a touched flag:
   * somebody who types a character and deletes it has changed nothing.
   */
  useEffect(() => {
    onDirtyChange?.(
      description !== (existing?.description ?? '') ||
        severity !== (existing?.severity ?? 'medium') ||
        assignee !== (existing?.assignee_id ?? '') ||
        due !== (existing?.due_date ?? ''),
    );
  }, [description, severity, assignee, due, existing, onDirtyChange]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!description.trim()) return;
    void onSubmit({
      description: description.trim(),
      severity,
      assignee_id: assignee || null,
      due_date: due || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>

      <div className="field">
        <label htmlFor="snag-description">What is wrong?</label>
        <textarea
          id="snag-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={4000}
          disabled={busy}
          placeholder="Grout line to the reception floor is uneven along the north edge"
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="snag-severity">How serious?</label>
          <select
            id="snag-severity"
            value={severity}
            disabled={busy}
            onChange={(e) => setSeverity(e.target.value as Severity)}
          >
            {SNAG_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABELS[s]}
              </option>
            ))}
          </select>
          <p className="hint">
            Decides how long it may stay open — Critical 2 days, High 5, Medium 14, Low 30.
          </p>
        </div>
        <div className="field">
          <label htmlFor="snag-assignee">Who is fixing it?</label>
          <select
            id="snag-assignee"
            value={assignee}
            disabled={busy}
            onChange={(e) => setAssignee(e.target.value)}
          >
            <option value="">Nobody yet</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.name}
              </option>
            ))}
          </select>
          <p className="hint">Naming somebody assigns it. They cannot check their own fix.</p>
        </div>
        <div className="field">
          <label htmlFor="snag-due">Due by</label>
          <input
            id="snag-due"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      {existing && (
        <p className="hint">
          Raised by {existing.raised_by_name ?? 'someone'} on {formatDate(existing.raised_at)}.
        </p>
      )}

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !description.trim()}>
          {busy ? 'Saving…' : existing ? 'Save' : 'Raise it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
